import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Calendar, Users, Clock, MapPin, Plus, CheckCircle, XCircle,
  Phone, Mail, Building, ArrowRight, Loader2, AlertTriangle,
  RefreshCw, UserCheck, FileText, ChevronLeft, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const visitStatuses = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-500', icon: Calendar },
  confirmed: { label: 'Confirmed', color: 'bg-green-500', icon: CheckCircle },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500', icon: Clock },
  completed: { label: 'Completed', color: 'bg-emerald-600', icon: CheckCircle },
  no_show: { label: 'No Show', color: 'bg-red-500', icon: XCircle },
  rescheduled: { label: 'Rescheduled', color: 'bg-orange-500', icon: RefreshCw },
  cancelled: { label: 'Cancelled', color: 'bg-slate-500', icon: XCircle },
};

const followUpTypes = [
  { value: 'call', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Users },
  { value: 'send_proposal', label: 'Send Proposal', icon: FileText },
  { value: 'site_visit', label: 'Site Visit', icon: Building },
];

const SalesVisits = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [visits, setVisits] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [stats, setStats] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddVisit, setShowAddVisit] = useState(false);
  const [showCompleteVisit, setShowCompleteVisit] = useState(null);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [newVisit, setNewVisit] = useState({
    title: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
    visit_type: 'office_visit',
    purpose: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    location: '',
    assigned_to: '',
    notes: ''
  });

  const [completeData, setCompleteData] = useState({
    outcome: 'positive',
    outcome_notes: '',
    create_follow_up: true,
    follow_up_type: 'call',
    follow_up_days: 3
  });

  const [newFollowUp, setNewFollowUp] = useState({
    follow_up_type: 'call',
    title: '',
    contact_name: '',
    due_date: '',
    priority: 'medium',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  // Progressive — tiles render as they arrive instead of blocking on the
  // slowest of the four parallel calls.
  const fetchData = () => {
    setLoading(true);
    const tasks = [
      axios.get(`${API}/visits?token=${token}`)
        .then((r) => setVisits(r.data.visits || []))
        .catch((e) => console.error('[SalesVisits] visits failed:', e)),
      axios.get(`${API}/follow-ups?token=${token}`)
        .then((r) => setFollowUps(r.data.follow_ups || []))
        .catch((e) => console.error('[SalesVisits] follow-ups failed:', e)),
      axios.get(`${API}/visits/stats?token=${token}`)
        .then((r) => setStats(r.data.stats || null))
        .catch((e) => console.error('[SalesVisits] stats failed:', e)),
      axios.get(`${API}/visits/team?token=${token}`)
        .then((r) => setTeamMembers(r.data.team || []))
        .catch((e) => console.error('[SalesVisits] team failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const handleCreateVisit = async () => {
    if (!newVisit.title || !newVisit.scheduled_date) {
      toast.error('Title and date are required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/visits?token=${token}`, newVisit);

      if (response.data.success) {
        toast.success('Visit scheduled!');
        setShowAddVisit(false);
        setNewVisit({
          title: '', contact_name: '', contact_email: '', contact_phone: '',
          company_name: '', visit_type: 'office_visit', purpose: '',
          scheduled_date: '', scheduled_time: '', duration_minutes: 60,
          location: '', assigned_to: '', notes: ''
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to schedule visit');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteVisit = async () => {
    if (!showCompleteVisit) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/visits/${showCompleteVisit.id}/complete?token=${token}`,
        completeData
      );

      if (response.data.success) {
        toast.success('Visit completed!');
        if (response.data.follow_up) {
          toast.info('Follow-up created automatically');
        }
        setShowCompleteVisit(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to complete visit');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFollowUp = async () => {
    if (!newFollowUp.title || !newFollowUp.due_date) {
      toast.error('Title and due date are required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/follow-ups?token=${token}`, newFollowUp);

      if (response.data.success) {
        toast.success('Follow-up created!');
        setShowAddFollowUp(false);
        setNewFollowUp({
          follow_up_type: 'call', title: '', contact_name: '',
          due_date: '', priority: 'medium', notes: ''
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create follow-up');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteFollowUp = async (followUpId, outcome) => {
    try {
      await axios.post(`${API}/follow-ups/${followUpId}/complete?token=${token}`, {
        outcome,
        outcome_notes: ''
      });
      toast.success('Follow-up completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete follow-up');
    }
  };

  const getStatusBadge = (status) => {
    const config = visitStatuses[status] || { label: status, color: 'bg-slate-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getTodayVisits = () => {
    const today = new Date().toISOString().split('T')[0];
    return visits.filter(v => v.scheduled_date === today && ['scheduled', 'confirmed'].includes(v.status));
  };

  const getUpcomingVisits = () => {
    const today = new Date().toISOString().split('T')[0];
    return visits.filter(v => v.scheduled_date >= today && ['scheduled', 'confirmed'].includes(v.status));
  };

  const getOverdueFollowUps = () => {
    const today = new Date().toISOString().split('T')[0];
    return followUps.filter(f => f.due_date < today && ['pending', 'in_progress'].includes(f.status));
  };

  return (
    <div className="space-y-6" data-testid="sales-visits-page">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-heading font-bold text-primary flex items-center gap-2">
            <Calendar className="text-accent" />
            Sales Visit Scheduling
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Schedule office visits and manage follow-ups
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddFollowUp(true)} variant="outline">
            <Clock className="mr-2" size={16} />
            New Follow-up
          </Button>
          <Button onClick={() => setShowAddVisit(true)} className="bg-accent">
            <Plus className="mr-2" size={16} />
            Schedule Visit
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today's Visits</p>
                  <p className="text-2xl font-bold">{getTodayVisits().length}</p>
                </div>
                <Calendar className="text-accent" size={28} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Upcoming</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.visits?.upcoming || 0}</p>
                </div>
                <Clock className="text-blue-500" size={28} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.visits?.completed || 0}</p>
                </div>
                <CheckCircle className="text-green-500" size={28} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Follow-ups</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.follow_ups?.pending || 0}</p>
                </div>
                <RefreshCw className="text-yellow-500" size={28} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Success Rate</p>
                  <p className="text-2xl font-bold text-accent">{stats.success_rate || 0}%</p>
                </div>
                <UserCheck className="text-accent" size={28} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Overdue Alert */}
      {getOverdueFollowUps().length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-red-500" size={24} />
              <div>
                <p className="font-medium text-red-700">
                  {getOverdueFollowUps().length} overdue follow-up{getOverdueFollowUps().length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600">Review and complete these as soon as possible</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="visits" className="w-full">
        <TabsList>
          <TabsTrigger value="visits">Visits ({visits.length})</TabsTrigger>
          <TabsTrigger value="follow-ups">
            Follow-ups ({followUps.length})
            {getOverdueFollowUps().length > 0 && (
              <Badge variant="destructive" className="ml-2 text-xs">{getOverdueFollowUps().length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
        </TabsList>

        {/* Visits Tab */}
        <TabsContent value="visits" className="mt-6">
          {visits.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-slate-500">No visits scheduled yet.</p>
                <Button onClick={() => setShowAddVisit(true)} className="mt-4">
                  <Plus className="mr-2" size={16} />
                  Schedule First Visit
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {visits.map((visit) => (
                <Card key={visit.id} className={visit.status === 'completed' ? 'opacity-75' : ''}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          visit.status === 'completed' ? 'bg-green-100' :
                          visit.status === 'scheduled' ? 'bg-blue-100' : 'bg-slate-100'
                        }`}>
                          <Calendar className={
                            visit.status === 'completed' ? 'text-green-600' :
                            visit.status === 'scheduled' ? 'text-blue-600' : 'text-slate-600'
                          } size={24} />
                        </div>
                        <div>
                          <h4 className="font-semibold">{visit.title}</h4>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Users size={14} />
                            <span>{visit.contact_name || 'No contact'}</span>
                            {visit.company_name && (
                              <>
                                <span>•</span>
                                <Building size={14} />
                                <span>{visit.company_name}</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {visit.scheduled_date}
                            </span>
                            {visit.scheduled_time && (
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {visit.scheduled_time}
                              </span>
                            )}
                            {visit.location && (
                              <span className="flex items-center gap-1">
                                <MapPin size={12} />
                                {visit.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(visit.status)}
                        {visit.assigned_to_name && (
                          <Badge variant="outline" className="text-xs">
                            <UserCheck size={12} className="mr-1" />
                            {visit.assigned_to_name}
                          </Badge>
                        )}
                        {['scheduled', 'confirmed'].includes(visit.status) && (
                          <Button
                            size="sm"
                            onClick={() => setShowCompleteVisit(visit)}
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Follow-ups Tab */}
        <TabsContent value="follow-ups" className="mt-6">
          {followUps.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-slate-500">No follow-ups yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {followUps.map((followUp) => {
                const isOverdue = followUp.is_overdue || (
                  followUp.due_date < new Date().toISOString().split('T')[0] &&
                  ['pending', 'in_progress'].includes(followUp.status)
                );
                
                return (
                  <Card key={followUp.id} className={isOverdue ? 'border-red-200' : ''}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isOverdue ? 'bg-red-100' :
                            followUp.status === 'completed' ? 'bg-green-100' : 'bg-blue-100'
                          }`}>
                            {followUpTypes.find(t => t.value === followUp.follow_up_type)?.icon && 
                              (() => {
                                const IconComponent = followUpTypes.find(t => t.value === followUp.follow_up_type)?.icon || Phone;
                                return <IconComponent className={
                                  isOverdue ? 'text-red-600' :
                                  followUp.status === 'completed' ? 'text-green-600' : 'text-blue-600'
                                } size={20} />;
                              })()
                            }
                          </div>
                          <div>
                            <h4 className="font-semibold flex items-center gap-2">
                              {followUp.title}
                              {isOverdue && (
                                <Badge variant="destructive" className="text-xs">Overdue</Badge>
                              )}
                            </h4>
                            <p className="text-sm text-slate-500">
                              {followUp.contact_name || 'No contact'} • Due: {followUp.due_date}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            followUp.priority === 'urgent' ? 'destructive' :
                            followUp.priority === 'high' ? 'default' : 'secondary'
                          }>
                            {followUp.priority}
                          </Badge>
                          {followUp.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCompleteFollowUp(followUp.id, 'completed')}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Done
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Calendar View Tab */}
        <TabsContent value="calendar" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Schedule Overview</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const date = new Date(selectedDate);
                      date.setDate(date.getDate() - 1);
                      setSelectedDate(date.toISOString().split('T')[0]);
                    }}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const date = new Date(selectedDate);
                      date.setDate(date.getDate() + 1);
                      setSelectedDate(date.toISOString().split('T')[0]);
                    }}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const dayVisits = visits.filter(v => v.scheduled_date === selectedDate);
                
                if (dayVisits.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Calendar className="mx-auto text-slate-300 mb-3" size={40} />
                      <p className="text-slate-500">No visits scheduled for this day</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-3">
                    {dayVisits.sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || '')).map((visit) => (
                      <div key={visit.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <div className="text-center min-w-[60px]">
                          <p className="font-bold text-accent">{visit.scheduled_time || '--:--'}</p>
                          <p className="text-xs text-slate-500">{visit.duration_minutes}min</p>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{visit.title}</p>
                          <p className="text-sm text-slate-500">{visit.contact_name}</p>
                        </div>
                        {getStatusBadge(visit.status)}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Visit Modal */}
      {showAddVisit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Schedule New Visit</CardTitle>
              <CardDescription>Book an office visit or appointment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Visit Title *</Label>
                  <Input
                    value={newVisit.title}
                    onChange={(e) => setNewVisit({ ...newVisit, title: e.target.value })}
                    placeholder="Initial consultation with ABC Corp"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={newVisit.contact_name}
                    onChange={(e) => setNewVisit({ ...newVisit, contact_name: e.target.value })}
                    placeholder="John Smith"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input
                    value={newVisit.company_name}
                    onChange={(e) => setNewVisit({ ...newVisit, company_name: e.target.value })}
                    placeholder="ABC Corporation"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newVisit.contact_email}
                    onChange={(e) => setNewVisit({ ...newVisit, contact_email: e.target.value })}
                    placeholder="john@abc.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newVisit.contact_phone}
                    onChange={(e) => setNewVisit({ ...newVisit, contact_phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={newVisit.scheduled_date}
                    onChange={(e) => setNewVisit({ ...newVisit, scheduled_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={newVisit.scheduled_time}
                    onChange={(e) => setNewVisit({ ...newVisit, scheduled_time: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Duration (min)</Label>
                  <Input
                    type="number"
                    value={newVisit.duration_minutes}
                    onChange={(e) => setNewVisit({ ...newVisit, duration_minutes: parseInt(e.target.value) })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Visit Type</Label>
                  <select
                    value={newVisit.visit_type}
                    onChange={(e) => setNewVisit({ ...newVisit, visit_type: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    <option value="office_visit">Office Visit</option>
                    <option value="site_visit">Site Visit</option>
                    <option value="virtual_meeting">Virtual Meeting</option>
                  </select>
                </div>
                <div>
                  <Label>Assign To</Label>
                  <select
                    value={newVisit.assigned_to}
                    onChange={(e) => setNewVisit({ ...newVisit, assigned_to: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    <option value="">Myself</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>{member.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Location</Label>
                <Input
                  value={newVisit.location}
                  onChange={(e) => setNewVisit({ ...newVisit, location: e.target.value })}
                  placeholder="Conference Room A / Client Office"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Purpose / Notes</Label>
                <textarea
                  value={newVisit.purpose}
                  onChange={(e) => setNewVisit({ ...newVisit, purpose: e.target.value })}
                  placeholder="Discuss property requirements, show portfolio..."
                  className="w-full mt-1 p-2 border rounded-lg min-h-[80px]"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddVisit(false)}>Cancel</Button>
                <Button onClick={handleCreateVisit} disabled={loading}>
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Schedule Visit
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Complete Visit Modal */}
      {showCompleteVisit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Complete Visit</CardTitle>
              <CardDescription>{showCompleteVisit.title}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Outcome *</Label>
                <select
                  value={completeData.outcome}
                  onChange={(e) => setCompleteData({ ...completeData, outcome: e.target.value })}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  <option value="positive">Positive - Interested, moving forward</option>
                  <option value="neutral">Neutral - Need more time/info</option>
                  <option value="negative">Negative - Not interested</option>
                  <option value="no_show">No Show - Client didn't attend</option>
                </select>
              </div>

              <div>
                <Label>Notes</Label>
                <textarea
                  value={completeData.outcome_notes}
                  onChange={(e) => setCompleteData({ ...completeData, outcome_notes: e.target.value })}
                  placeholder="Key takeaways from the meeting..."
                  className="w-full mt-1 p-2 border rounded-lg min-h-[80px]"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Create Follow-up</p>
                  <p className="text-xs text-slate-500">Auto-create a follow-up task</p>
                </div>
                <input
                  type="checkbox"
                  checked={completeData.create_follow_up}
                  onChange={(e) => setCompleteData({ ...completeData, create_follow_up: e.target.checked })}
                  className="w-5 h-5"
                />
              </div>

              {completeData.create_follow_up && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Follow-up Type</Label>
                    <select
                      value={completeData.follow_up_type}
                      onChange={(e) => setCompleteData({ ...completeData, follow_up_type: e.target.value })}
                      className="w-full mt-1 p-2 border rounded-lg text-sm"
                    >
                      {followUpTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Follow-up in (days)</Label>
                    <Input
                      type="number"
                      value={completeData.follow_up_days}
                      onChange={(e) => setCompleteData({ ...completeData, follow_up_days: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCompleteVisit(null)}>Cancel</Button>
                <Button onClick={handleCompleteVisit} disabled={loading}>
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Complete Visit
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Follow-up Modal */}
      {showAddFollowUp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Create Follow-up</CardTitle>
              <CardDescription>Schedule a follow-up task</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={newFollowUp.title}
                  onChange={(e) => setNewFollowUp({ ...newFollowUp, title: e.target.value })}
                  placeholder="Follow up with John about proposal"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <select
                    value={newFollowUp.follow_up_type}
                    onChange={(e) => setNewFollowUp({ ...newFollowUp, follow_up_type: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    {followUpTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <select
                    value={newFollowUp.priority}
                    onChange={(e) => setNewFollowUp({ ...newFollowUp, priority: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={newFollowUp.contact_name}
                    onChange={(e) => setNewFollowUp({ ...newFollowUp, contact_name: e.target.value })}
                    placeholder="John Smith"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Due Date *</Label>
                  <Input
                    type="date"
                    value={newFollowUp.due_date}
                    onChange={(e) => setNewFollowUp({ ...newFollowUp, due_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <textarea
                  value={newFollowUp.notes}
                  onChange={(e) => setNewFollowUp({ ...newFollowUp, notes: e.target.value })}
                  placeholder="Key points to discuss..."
                  className="w-full mt-1 p-2 border rounded-lg min-h-[60px]"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddFollowUp(false)}>Cancel</Button>
                <Button onClick={handleCreateFollowUp} disabled={loading}>
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Create Follow-up
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SalesVisits;
